import { Avatar as RadixAvatar } from "radix-ui";
import type { ReactNode } from "react";
import { cn } from "../lib/cn";

export type AvatarSize = 24 | 32 | 40 | 48 | 56 | 72 | 96;

/**
 * Размер подписи внутри аватара. В DS инициалы были фиксированные 13px на всех
 * размерах — на 72 и 96 это выглядит как опечатка, поэтому шкала продолжена
 * пропорционально: подпись занимает примерно треть диаметра.
 */
const INITIALS_CLASS: Record<AvatarSize, string> = {
	24: "text-[11px]",
	32: "text-[13px]",
	40: "text-[14px]",
	48: "text-[16px]",
	56: "text-[18px]",
	72: "text-[22px]",
	96: "text-[28px]",
};

const BOX_CLASS: Record<AvatarSize, string> = {
	24: "size-6",
	32: "size-8",
	40: "size-10",
	48: "size-12",
	56: "size-14",
	72: "size-[72px]",
	96: "size-24",
};

export interface AvatarIconProps {
	size?: AvatarSize;
	/** `circle` — человек, `square` (r10) — сервис или программа. */
	form?: "circle" | "square";
	src?: string;
	alt?: string;
	/** Фолбэк, когда фото нет. Две буквы — так их набирает вся экосистема. */
	initials?: string;
	/** Глиф вместо инициалов: аватар сервиса, а не человека. */
	icon?: ReactNode;
	online?: boolean;
	/**
	 * Сколько ждать картинку, прежде чем показать фолбэк, мс.
	 *
	 * Умолчание 200 — «мигни инициалами, пока грузится фото». Там, где картинка
	 * ГАРАНТИРОВАНА (у нас это `/api/avatar/{userId}`: обработчик отдаёт фото или
	 * рисует заглушку сам), это вредно: инициалы конкурируют с нашей же
	 * заглушкой и дают экосистеме два разных «нет фото». Такие места ставят
	 * задержку больше, и тогда фолбэк остаётся тем, чем должен быть, — реакцией
	 * на сетевую ошибку.
	 */
	fallbackDelayMs?: number;
	className?: string;
}

/**
 * Аватар с фолбэком на инициалы. Онлайн-точка — четверть диаметра, с кольцом
 * 1.5px в цвет карточки: без кольца она сливается с тёмным краем фото.
 */
export function AvatarIcon({
	size = 48,
	form = "circle",
	src,
	alt = "",
	initials = "",
	icon,
	online = false,
	fallbackDelayMs,
	className,
}: AvatarIconProps) {
	return (
		<span
			className={cn(
				"relative inline-flex shrink-0 items-center justify-center",
				BOX_CLASS[size],
				className,
			)}
		>
			<RadixAvatar.Root
				className={cn(
					"flex size-full items-center justify-center overflow-hidden bg-sunken text-fg",
					form === "circle" ? "rounded-full" : "rounded-control",
				)}
			>
				{src ? (
					<RadixAvatar.Image
						src={src}
						alt={alt}
						width={size}
						height={size}
						className="size-full object-cover"
					/>
				) : null}
				<RadixAvatar.Fallback
					delayMs={src ? (fallbackDelayMs ?? 200) : 0}
					className="flex size-full items-center justify-center font-mono"
				>
					{icon ?? <span className={INITIALS_CLASS[size]}>{initials}</span>}
				</RadixAvatar.Fallback>
			</RadixAvatar.Root>
			{online ? (
				<span
					aria-hidden="true"
					className="absolute right-0 bottom-0 rounded-full bg-brand ring-[1.5px] ring-card"
					style={{ width: Math.round(size * 0.25), height: Math.round(size * 0.25) }}
				/>
			) : null}
		</span>
	);
}
